import { useState } from 'react';
import { trpc } from '../lib/trpc';
import { useAsync } from '../lib/useAsync';
import { Loading, ErrorBox, EmptyBox } from '../components/admin/ui';
import { Tooltip } from '../components/Tooltip';
import { TOOLTIPS, type TooltipKey } from '../lib/tooltips';

/**
 * Tela /comunicacoes (cliente): fila de aprovação (Fase 3, B7). Pendentes com
 * preview, aprovar/rejeitar individual e em lote, histórico com filtro por
 * status. Estados vazio/carregando/erro. Tokens canônicos (tema claro).
 */

const STATUS_META = {
  aguardando_aprovacao: { label: 'Aguardando', cor: 'var(--amber)', tip: 'comStatusAguardando' },
  enviada: { label: 'Enviada', cor: 'var(--accent)', tip: 'comStatusEnviada' },
  simulada: { label: 'Simulada (sombra)', cor: 'var(--ink-3)', tip: 'comStatusSimulada' },
  aprovada: { label: 'Aprovada', cor: 'var(--accent)' },
  rejeitada: { label: 'Rejeitada', cor: 'var(--ink-4)', tip: 'comStatusRejeitada' },
  falhou: { label: 'Falhou', cor: 'var(--risk)', tip: 'comStatusFalhou' },
  rascunho: { label: 'Rascunho', cor: 'var(--ink-4)' },
} as const satisfies Record<string, { label: string; cor: string; tip?: TooltipKey }>;
type StatusTipo = keyof typeof STATUS_META;

const GATILHO_LABEL: Record<string, string> = {
  reajuste_em_30d: 'Reajuste em 30 dias',
  parcela_atrasada: 'Parcela atrasada',
};

export function ComunicacoesPage() {
  const [status, setStatus] = useState<'' | StatusTipo>('aguardando_aprovacao');
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());

  function alternarExpandido(id: string) {
    setExpandidos((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  const { data, loading, error, reload } = useAsync(
    () => trpc.comunicacao.list.query({ status: status || undefined }),
    [status],
  );

  function toggle(id: string) {
    setSel((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }
  async function acao(fn: () => Promise<unknown>) {
    setBusy(true);
    try {
      await fn();
      setSel(new Set());
      reload();
    } finally {
      setBusy(false);
    }
  }

  const podeAgir = (s: string) => s === 'aguardando_aprovacao';

  return (
    <div className="mx-auto max-w-4xl" data-testid="comunicacoes-page">
      <h1 className="text-2xl font-semibold text-ink">Comunicações</h1>
      <p className="mt-1 text-sm text-ink-3">Aprove os e-mails antes do envio. Nada sai sem sua aprovação.</p>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <label className="flex flex-col gap-1 text-xs text-ink-3">
          Status
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as '' | StatusTipo)}
            className="h-11 rounded-[10px] border border-line bg-surface px-3 text-sm text-ink md:h-10"
            data-testid="comunicacoes-filtro"
          >
            <option value="">Todas</option>
            <option value="aguardando_aprovacao">Aguardando aprovação</option>
            <option value="enviada">Enviadas</option>
            <option value="simulada">Simuladas (sombra)</option>
            <option value="rejeitada">Rejeitadas</option>
            <option value="falhou">Falharam</option>
          </select>
        </label>
        {sel.size > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-ink-3">{sel.size} selecionada(s)</span>
            <button
              type="button"
              disabled={busy}
              onClick={() => acao(() => trpc.comunicacao.aprovarLote.mutate({ ids: [...sel] }))}
              className="flex h-11 items-center rounded-[10px] px-3 text-sm font-semibold text-white disabled:opacity-60 md:h-9"
              style={{ background: 'var(--accent)' }}
            >
              Aprovar em lote
            </button>
            <Tooltip text={TOOLTIPS.comAprovarLote} label="O que faz aprovar em lote" />
            <button
              type="button"
              disabled={busy}
              onClick={() => acao(() => trpc.comunicacao.rejeitarLote.mutate({ ids: [...sel] }))}
              className="flex h-11 items-center rounded-[10px] border border-line px-3 text-sm text-ink-2 disabled:opacity-60 md:h-9"
            >
              Rejeitar em lote
            </button>
          </div>
        )}
      </div>

      <div className="mt-4">
        {loading && <Loading />}
        {error && <ErrorBox msg={error} onRetry={reload} />}
        {data && data.length === 0 && (
          <EmptyBox titulo="Nada por aqui" descricao="Nenhuma comunicação neste filtro. A varredura gera novas conforme o risco." />
        )}
        {data && data.length > 0 && (
          <ul className="space-y-3">
            {data.map((c) => {
              const meta = STATUS_META[c.status as StatusTipo] ?? STATUS_META.rascunho;
              return (
                <li key={c.id} className="card p-4" data-testid="comunicacao-item">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                    <div className="flex min-w-0 flex-1 items-start gap-3">
                      {podeAgir(c.status) && (
                        <input
                          type="checkbox"
                          checked={sel.has(c.id)}
                          onChange={() => toggle(c.id)}
                          className="mt-1"
                          aria-label="selecionar"
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span
                            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold"
                            style={{ background: 'var(--surface-2)', color: meta.cor }}
                          >
                            {meta.label}
                            {'tip' in meta && meta.tip && (
                              <Tooltip text={TOOLTIPS[meta.tip]} label={`O que significa ${meta.label}`} />
                            )}
                          </span>
                          <span className="text-xs text-ink-4">{GATILHO_LABEL[c.gatilho] ?? c.gatilho}</span>
                          <span className="text-sm font-medium text-ink">{c.compradorNome}</span>
                          <span className="text-xs text-ink-3">· {c.unidade}</span>
                        </div>
                        <div className="mt-1.5 text-sm font-medium text-ink">{c.assunto}</div>
                        {c.status === 'falhou' && c.erroEnvio && (
                          <p className="mt-1 text-xs" style={{ color: 'var(--risk)' }} data-testid="comunicacao-erro">
                            Motivo: {c.erroEnvio}
                          </p>
                        )}
                        <p
                          className={`mt-1 whitespace-pre-line text-sm text-ink-3 ${expandidos.has(c.id) ? '' : 'line-clamp-3'}`}
                          data-testid="comunicacao-corpo"
                        >
                          {c.corpo}
                        </p>
                        {c.corpo.length > 180 && (
                          <button
                            type="button"
                            onClick={() => alternarExpandido(c.id)}
                            className="mt-1 text-xs font-medium text-accent hover:underline"
                          >
                            {expandidos.has(c.id) ? 'Ver menos' : 'Ver mensagem completa'}
                          </button>
                        )}
                      </div>
                    </div>
                    {podeAgir(c.status) && (
                      <div className="flex gap-2 sm:shrink-0 sm:flex-col sm:gap-1.5">
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => acao(() => trpc.comunicacao.aprovar.mutate({ id: c.id }))}
                          className="flex h-11 flex-1 items-center justify-center rounded-[10px] px-3 text-xs font-semibold text-white disabled:opacity-60 sm:h-auto sm:flex-none sm:justify-start sm:py-1.5"
                          style={{ background: 'var(--accent)' }}
                        >
                          Aprovar
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => acao(() => trpc.comunicacao.rejeitar.mutate({ id: c.id }))}
                          className="flex h-11 flex-1 items-center justify-center rounded-[10px] border border-line px-3 text-xs text-ink-2 disabled:opacity-60 sm:h-auto sm:flex-none sm:justify-start sm:py-1.5"
                        >
                          Rejeitar
                        </button>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
